import { useState } from "react";
import type { Dispatch } from "react";
import type { ReplayEvent, ReplayState } from "../state/replay";
import type { ReplayPayload } from "../types";
import { MetricsView } from "./MetricsView";
import { ReplayView } from "./ReplayView";
import { StoryView } from "./StoryView";
import { TimelineView } from "./TimelineView";

const TABS = ["replay", "timeline", "story", "metrics"] as const;
type Tab = (typeof TABS)[number];

interface ResultsPaneProps {
  replay: ReplayPayload | null;
  loading: boolean;
  state: ReplayState;
  dispatch: Dispatch<ReplayEvent>;
  runId: string | null;
}

export function ResultsPane({ replay, loading, state, dispatch, runId }: ResultsPaneProps) {
  const [tab, setTab] = useState<Tab>("replay");
  const manifest = replay?.manifest;
  const mismatch =
    manifest?.rematerialized && manifest.recorded_digest && manifest.recorded_digest !== manifest.digest;
  const seek = (tick: number) => dispatch({ type: "seek", tick });
  return (
    <section className="results-pane">
      <nav className="tabs" role="tablist">
        {TABS.map((name) => (
          <button
            key={name}
            role="tab"
            aria-selected={tab === name}
            className={tab === name ? "active" : ""}
            onClick={() => setTab(name)}
          >
            {name}
          </button>
        ))}
      </nav>
      {mismatch && manifest && (
        <div className="banner">
          recorded under engine {manifest.recorded_engine ?? "?"} — digest differs; replay was
          re-materialized with the current engine
        </div>
      )}
      {loading && <div className="pane-empty">materializing replay…</div>}
      {!loading && !replay && <div className="pane-empty">run a definition to see results</div>}
      {!loading && replay && (
        <div className="results-body">
          {tab === "replay" && <ReplayView replay={replay} state={state} dispatch={dispatch} runId={runId} />}
          {tab === "timeline" && (
            <TimelineView
              replay={replay}
              tick={state.tick}
              onSeek={seek}
              focus={state.focus}
              onFocus={(agent) => dispatch({ type: "focus", agent })}
            />
          )}
          {tab === "story" && <StoryView replay={replay} tick={state.tick} onSeek={seek} />}
          {tab === "metrics" && <MetricsView replay={replay} tick={state.tick} />}
        </div>
      )}
    </section>
  );
}
